import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight, ArrowUp, ArrowDown } from 'lucide-react';

function formatCurrency(value) {
  if (value == null) return '-';
  return '$' + Number(value).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 });
}

function formatCellValue(value, colKey) {
  if (value == null || value === '') return '-';
  if (colKey === 'amount') return formatCurrency(value);
  return String(value);
}

export default function TableBuilderView({
  data,
  columns,
  pagination,
  onPageChange,
  sortBy,
  sortDir,
  onSort,
  groupBy,
  dataSource,
}) {
  if (!data || data.length === 0) {
    return <div className="report-chart-empty">No records for the selected configuration.</div>;
  }

  // Group data if groupBy is set
  const groups = useMemo(() => {
    if (!groupBy || !data) return null;
    const map = new Map();
    data.forEach(row => {
      const key = row[groupBy] || row[`${groupBy}_name`] || 'Unassigned';
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(row);
    });
    return map;
  }, [data, groupBy]);

  const handleSort = (colKey) => {
    if (onSort) onSort(colKey);
  };

  const renderSortIcon = (colKey) => {
    if (sortBy !== colKey) return null;
    return sortDir === 'asc'
      ? <ArrowUp size={12} style={{ marginLeft: 4 }} />
      : <ArrowDown size={12} style={{ marginLeft: 4 }} />;
  };

  const getCellValue = (row, col) => {
    // Try direct key, then alias patterns
    return row[col.key] ?? row[`${col.key}_name`] ?? row[col.key.replace(/_/g, '')] ?? '-';
  };

  const renderRow = (row, isOrders) => (
    <tr key={row.id}>
      {columns.map(col => {
        const value = getCellValue(row, col);
        const isName = col.key === 'name';
        return (
          <td key={col.key} style={{ textAlign: col.key === 'amount' ? 'right' : 'left' }}>
            {isName ? (
              <Link to={isOrders ? `/orders/${row.id}` : `/opportunities/${row.id}`}>
                {formatCellValue(value, col.key)}
              </Link>
            ) : (
              formatCellValue(value, col.key)
            )}
          </td>
        );
      })}
    </tr>
  );

  const isOrders = dataSource === 'orders';

  const renderGroupSubtotal = (rows) => {
    const amountCol = columns.find(c => c.key === 'amount');
    if (!amountCol) return null;
    const total = rows.reduce((sum, row) => sum + (Number(row.amount) || 0), 0);
    return (
      <tr className="matrix-group-row">
        {columns.map((col, i) => (
          <td key={col.key} style={{ textAlign: col.key === 'amount' ? 'right' : 'left', fontWeight: 600 }}>
            {col.key === 'amount' ? formatCurrency(total) : (i === 0 ? `Subtotal (${rows.length})` : '')}
          </td>
        ))}
      </tr>
    );
  };

  return (
    <>
      <div className="matrix-table-wrapper">
        <table className="matrix-table">
          <thead>
            <tr>
              {columns.map(col => (
                <th
                  key={col.key}
                  style={{ cursor: 'pointer', textAlign: col.key === 'amount' ? 'right' : 'left' }}
                  onClick={() => handleSort(col.key)}
                >
                  {col.label}
                  {renderSortIcon(col.key)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {groups ? (
              [...groups.entries()].map(([groupLabel, rows]) => (
                <React.Fragment key={groupLabel}>
                  <tr className="matrix-group-row">
                    <td colSpan={columns.length} style={{ fontWeight: 700, paddingLeft: 14 }}>
                      {groupLabel}
                    </td>
                  </tr>
                  {rows.map(row => renderRow(row, isOrders))}
                  {renderGroupSubtotal(rows)}
                </React.Fragment>
              ))
            ) : (
              data.map(row => renderRow(row, isOrders))
            )}
          </tbody>
        </table>
      </div>

      {pagination && pagination.totalPages > 1 && (
        <div className="drill-down-pagination">
          <button
            className="btn btn-secondary btn-sm"
            disabled={pagination.page <= 1}
            onClick={() => onPageChange(pagination.page - 1)}
          >
            <ChevronLeft size={14} /> Prev
          </button>
          <span>Page {pagination.page} of {pagination.totalPages} ({pagination.total} records)</span>
          <button
            className="btn btn-secondary btn-sm"
            disabled={pagination.page >= pagination.totalPages}
            onClick={() => onPageChange(pagination.page + 1)}
          >
            Next <ChevronRight size={14} />
          </button>
        </div>
      )}
    </>
  );
}
